import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Pause, ChevronDown } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { WordTitle } from "./WordTitle";
import { IMAGES } from "../../constants/images";

export const VideoHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const dRef = useRef(null);
  const mRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const clip = useTransform(scrollYProgress, [0, 1], ["inset(0% 0% 0% 0% round 0px)", "inset(8% 5% 8% 5% round 12px)"]);
  const dim = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);
  const brandScale = useTransform(scrollYProgress, [0, 0.45], [1, 0.3]);
  const brandOpacity = useTransform(scrollYProgress, [0, 0.3, 0.42], [1, 0.5, 0]);
  const brandY = useTransform(scrollYProgress, [0, 0.45], [0, -140]);

  const toggle = () => {
    [dRef.current, mRef.current].forEach((v) => {
      if (!v) return;
      playing ? v.pause() : v.play().catch(() => {});
    });
    setPlaying(!playing);
  };

  const onTime = (e) => {
    const v = e.target;
    if (v.duration) setProgress(v.currentTime / v.duration);
  };

  const seek = (e) => {
    const val = Number(e.target.value) / 100;
    [dRef.current, mRef.current].forEach((v) => {
      if (v && v.duration) v.currentTime = v.duration * val;
    });
    setProgress(val);
  };

  return (
    <section ref={ref} className="relative h-[115vh]" data-testid="hero-section">
      <div className="sticky top-0 h-screen overflow-hidden">
        <motion.div style={{ clipPath: clip, opacity: dim }} className="absolute inset-0 bg-black">
          <img src={IMAGES.hero_studio} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          <video
            ref={dRef}
            src="/videos/hero-desktop.mp4"
            poster={IMAGES.hero_studio}
            autoPlay
            loop
            muted
            playsInline
            onTimeUpdate={onTime}
            className="hidden md:block absolute inset-0 w-full h-full object-cover opacity-70"
            aria-label="Apex Ride studio video"
          />
          <video
            ref={mRef}
            src="/videos/hero-mobile.mp4"
            poster={IMAGES.hero_studio}
            autoPlay
            loop
            muted
            playsInline
            className="md:hidden absolute inset-0 w-full h-full object-cover opacity-70"
            aria-label="Apex Ride studio video"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black/85" />
        </motion.div>

        <div className="absolute inset-0 z-10 flex flex-col items-center justify-end px-6 pointer-events-none">
          <h1 className="font-serif uppercase text-white text-center text-xl md:text-[1.65rem] tracking-[0.05em] leading-snug max-w-4xl">
            <WordTitle lines={t.heroTitle} delay={1.7} mode="mount" />
          </h1>
          <motion.div style={{ opacity: cueOpacity }} className="mt-7">
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
              <ChevronDown className="text-[#C5A059]" size={24} data-testid="scroll-cue" />
            </motion.div>
          </motion.div>
          <motion.div
            style={{ scale: brandScale, opacity: brandOpacity, y: brandY }}
            className="origin-bottom mt-5 pb-2 w-full text-center select-none"
            data-testid="hero-wordmark"
            aria-hidden="true"
          >
            <motion.span
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4, delay: 2.6, ease: [0.19, 1, 0.22, 1] }}
              className="block font-serif text-[15vw] md:text-[12vw] leading-[0.75] text-white whitespace-nowrap"
            >
              APEX <em className="italic text-[#C5A059]">Ride</em>
            </motion.span>
          </motion.div>
        </div>

        <div
          className="absolute right-6 md:right-14 bottom-[calc(15vw+2rem)] md:bottom-[calc(12vw+2.5rem)] z-20 flex items-center gap-4"
          data-testid="video-controls"
        >
          <button
            onClick={toggle}
            data-testid="video-play-btn"
            aria-label={playing ? "Pause video" : "Play video"}
            className="w-10 h-10 rounded-full border border-white/40 flex items-center justify-center text-white hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={progress * 100}
            onChange={seek}
            aria-label="Video progress"
            data-testid="video-progress-slider"
            className="video-slider"
            style={{
              background: `linear-gradient(to right, #C5A059 ${progress * 100}%, rgba(255,255,255,0.25) ${progress * 100}%)`,
            }}
          />
        </div>
      </div>
    </section>
  );
};
